import { useNavigate } from 'react-router-dom';
import { Pin } from 'lucide-react';
import { Badge } from '../atoms/Badge';
import { StatsRow } from '../atoms/StatsRow';
import { TickerTag } from '../atoms/TickerTag';
import { LevelBadge } from '../atoms/LevelBadge';
import { AdminBadge } from '../atoms/AdminBadge';
import { Avatar } from '../atoms/Avatar';
import { timeAgo } from '@/lib/format';

export interface Post {
  id: number;
  board_id: number;
  board_name?: string;
  board_slug?: string;
  user_id?: string;
  title: string;
  content: string;
  post_type: string;
  ticker?: string | null;
  view_count: number;
  like_count: number;
  dislike_count?: number;
  comment_count: number;
  is_editor_pick?: boolean;
  is_ai_news?: boolean;
  is_pinned?: boolean;
  /** 비공개 글 (건의/제안 13 전용) — 본인/admin 만 조회 */
  is_private?: boolean;
  /** 이벤트 게시판(14) 자동 생성 글에 매핑된 event id — PostCard 가 /events/:id 로 라우팅 */
  event_id?: number | null;
  author_nickname: string;
  author_level: number;
  author_image?: string | null;
  /** super / content / readonly (admin_role) — 관리자 배지 노출용 */
  author_admin_role?: string | null;
  /** 테스트 계정 여부 — [TEST] 배지 노출용 */
  author_is_test?: boolean | null;
  created_at: string;
  /** 게시판 카테고리 (자유게시판 등) — 서버 응답에 있을 수 있음 */
  category_id?: number | null;
  category_name?: string | null;
  /** 공시(notice) 게시판 전용 — 분석 완료 여부.
      board_id=3 이외 게시판에서는 항상 false. 리스트의 '분석' 컬럼에 사용. */
  analyzed?: boolean;
  /** 공시(notice) 게시판 전용 — 중요도. SKIP 은 목록에서 제외되어 나타나지 않음.
      제목 앞 [중요]/[분석]/[일반] 배지에 사용. */
  importance?: 'CRITICAL' | 'IMPORTANT' | 'NORMAL' | null;
}

interface PostCardProps {
  post: Post;
  showBoard?: boolean;
  compact?: boolean;
}

const POST_TYPE_LABELS: Record<string, { label: string; variant: 'rise' | 'fall' | 'default' }> = {
  profit: { label: '수익', variant: 'rise' },
  loss: { label: '손실', variant: 'fall' },
  buy: { label: '매수', variant: 'rise' },
  sell: { label: '매도', variant: 'fall' },
};

export function PostCard({ post, compact }: PostCardProps) {
  const navigate = useNavigate();
  /* 이벤트 게시판 자동 연계 글은 EventDetailPage 로 통일. 그 외는 기존 PostDetail. */
  const handleClick = () =>
    navigate(post.event_id ? `/events/${post.event_id}` : `/post/${post.id}`);

  if (compact) {
    return (
      <button
        onClick={handleClick}
        className="flex w-full min-w-0 items-start gap-2 overflow-hidden rounded px-2 py-1.5 text-left hover:bg-[var(--color-bg)]"
      >
        <span className="min-w-0 flex-1 overflow-hidden truncate text-sm">{post.title}</span>
        <span className="shrink-0 whitespace-nowrap text-xs text-[var(--color-text-sub)]">
          💬{post.comment_count} 👍{post.like_count}
        </span>
      </button>
    );
  }

  return (
    <article
      onClick={handleClick}
      className="min-w-0 cursor-pointer overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4 transition-colors hover:border-[var(--color-primary)]/40"
    >
      {/*
       * 게시글 헤더 — 게시판명 태그 사용 금지 (시세공간 전역 정책)
       * 종목 배지/에디터픽/AI 뉴스 같은 기능 배지만 노출
       */}
      <div className="mb-2 flex items-center gap-2 text-xs">
        {post.is_pinned && (
          <span className="badge badge-primary">
            <Pin className="mr-0.5 h-3 w-3" /> 고정
          </span>
        )}
        {POST_TYPE_LABELS[post.post_type] && (
          <Badge variant={POST_TYPE_LABELS[post.post_type].variant}>
            {POST_TYPE_LABELS[post.post_type].label}
          </Badge>
        )}
        {post.is_editor_pick && <Badge variant="editor">에디터픽</Badge>}
        {post.is_ai_news && <Badge variant="primary">AI 증시뉴스</Badge>}
      </div>

      {/* 제목 */}
      <h3 className="mb-1 min-w-0 overflow-hidden truncate text-base font-semibold">{post.title}</h3>

      {/* 종목 태그 */}
      {post.ticker && (
        <div className="mb-2">
          <TickerTag ticker={post.ticker} />
        </div>
      )}

      {/* 작성자 + 통계
          모바일: 두 줄 — [아바타·닉네임·레벨·관리자]  [작성일]  ↵  [통계 우측정렬]
          sm+ : 한 줄 — [아바타·닉네임·레벨·관리자·작성일]                  [통계] */}
      <div className="mt-3 flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
        <div className="flex items-center justify-between gap-2 sm:justify-start">
          <div className="flex min-w-0 items-center gap-2">
            <Avatar
              src={post.author_image ?? undefined}
              alt={post.author_nickname}
              size="xs"
              userId={post.user_id}
            />
            <span className="block max-w-[128px] truncate text-xs font-medium">
              {post.author_nickname}
            </span>
            <AdminBadge role={post.author_admin_role} />
            <LevelBadge level={post.author_level} size="sm" />
          </div>
          <span className="shrink-0 whitespace-nowrap text-xs text-[var(--color-text-sub)]">
            <span className="hidden sm:inline">· </span>
            {timeAgo(post.created_at)}
          </span>
        </div>
        <div className="flex justify-end sm:justify-start">
          <StatsRow
            views={post.view_count}
            comments={post.comment_count}
            likes={post.like_count ?? 0}
            dislikes={post.dislike_count ?? 0}
          />
        </div>
      </div>
    </article>
  );
}
